import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmationModel from "../../../components/common/ConfirmationModel";
import {
  DatePicker,
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
  SelectWithLabel,
} from "../../../components/micro/input";
import Breadcrumb from "../../../components/common/BreadCrumb";
import dayjs from "dayjs";
import { Close, Plus } from "../../../assets/svg";
import { useDispatch, useSelector } from "react-redux";
import { createEquipment, getOutlet } from "../../../redux/companies/slice";
import { getProductList } from "../../../redux/product/slice";
import { IconButton } from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import moment from "moment";

const AddNewEquipment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { mode } = location.state || {};
  const isEditMode = location?.state?.mode;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const equipmentData = location.state.equipment || "";
  const storedData = sessionStorage.getItem("companyData");
  const companyData = storedData ? JSON.parse(storedData)?.company : null;
  const outletData = location?.state?.outlet;

  // Selector
  const { productsList, outletList } = useSelector((store) => ({
    productsList: store?.productsData?.getProductListStatus?.data?.data,
    outletList: store?.companiesData?.getOutletStatus?.data?.data,
  }));

  useEffect(() => {
    dispatch(
      getProductList({
        pageLimit: "all",
        search: "",
        sort_by: "name",
        sort_direction: "asc",
      })
    );
    dispatch(
      getOutlet({
        search: "",
        pageLimit: "all",
        company_id: companyData?.id || equipmentData?.company_id || "",
        sort_by: "name",
        sort_direction: "asc",
      })
    );
  }, [dispatch]);

  const options = (outletList?.data || outletList)?.map((outlet) => ({
    value: outlet.id,
    label: outlet.name,
  }));

  const productOptions = Array.isArray(productsList?.data || productsList)
    ? (productsList?.data || productsList)
        .filter((product) => product.is_active === 1)
        .map((product) => ({
          value: product.id,
          label: product.name,
        }))
    : [];

  const validationSchema = Yup.object({
    equipments: Yup.array().of(
      Yup.object({
        outlet_id: Yup.string().required("Please select outlet"),
        product_id: Yup.string().required("Please select equipment"),
        on_site_name: Yup.string().trim().required("Please enter on-site name"),
      })
    ),
  });

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    const payload = {
      equipment: values.equipments.map((equipment) => ({
        company_id:
          equipmentData?.company_id ||
          outletData?.company_id ||
          companyData?.id,
        outlet_id: equipment.outlet_id,
        on_site_name: equipment.on_site_name,
        product_id: equipment.product_id,
        purchase_date: dayjs(equipment.purchase_date).format("DD-MM-YYYY"),
        include_warranty: equipment.warranty ? 1 : 0,
        warranty_period: equipment.warrantyPeriod || null,
        serial_number: equipment.serialNumber || null,
      })),
    };

    const action = equipmentData?.id
      ? createEquipment({ id: equipmentData.id, ...payload })
      : createEquipment(payload);

    setIsLoading(true);
    try {
      const res = await dispatch(action);
      if (res.type === "createEquipment/fulfilled") {
        navigate(-1);
      }
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
    }
  };

  return (
    <div
      className={mode === "editOutlet" ? "model relative" : ""}
      style={mode === "editOutlet" ? { zIndex: 1300 } : {}}
    >
      {mode !== "editOutlet" && (
        <div className="mb-5">
          <h6 className="font-Montserrat font-normal text-xl mb-3">
            Company & Outlets
          </h6>
          <Breadcrumb
            breadCrumbParent={"Home"}
            breadCrumbActive={"Add New Equipments"}
            breadCrumbParent2={"Company & Outlets"}
            breadCrumbTitleKey={"/"}
            classes=""
          />
        </div>
      )}
      <div className={mode === "editOutlet" ? "w-2/3 h-2/3 overflow-auto" : ""}>
        <Formik
          initialValues={{
            equipments: [
              {
                outlet_id: outletData?.id || equipmentData?.outlet_id || "",
                product_id: equipmentData ? equipmentData.product_id : "",
                on_site_name: equipmentData ? equipmentData.on_site_name : "",
                purchase_date: equipmentData
                  ? equipmentData.purchase_date
                  : dayjs(),
                warranty: equipmentData
                  ? equipmentData.include_warranty
                  : false,
                warrantyPeriod: equipmentData
                  ? equipmentData.warranty_period
                  : "",
                serialNumber: equipmentData ? equipmentData.serial_number : "",
              },
            ],
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setFormValues(values);
            handleEditClick();
            setShowConfirmationModal(true);
          }}
        >
          {({
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            values,
          }) => (
            <Form onSubmit={handleSubmit}>
              <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mb-4">
                <h1 className="text-base font-normal mb-1 font-Proxima">
                  {companyData?.name}
                </h1>
                <h2 className="text-xl font-medium mb-4 font-Montserrat">
                  {isEditMode === "edit"
                    ? "Edit Equipments"
                    : "Add New Equipments"}
                </h2>
                <div>
                  <h3 className="text-xs font-normal mb-3">Equipments</h3>
                  {values.equipments.map((equipment, index) => (
                    <div
                      key={index}
                      className={`relative bg-white border border-borderLine rounded-lg p-4 grid grid-cols-1 gap-4 mb-4 ${
                        index !== 0 ? "mr-10" : "w-full"
                      }`}
                    >
                      {index !== 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newEquipments = [...values.equipments];
                            newEquipments.splice(index, 1);
                            handleChange({
                              target: {
                                name: "equipments",
                                value: newEquipments,
                              },
                            });
                          }}
                          className="absolute top-0 -right-8 py-2 px-1 text-red-500"
                        >
                          <Close />
                        </button>
                      )}
                      <div>
                        <SelectWithLabel
                          idFromName={`equipments[${index}].outlet_id`}
                          placeholder="Select Outlet"
                          options={options}
                          value={
                            outletData
                              ? outletData.id
                              : equipment.outlet_id || ""
                          }
                          setFieldValue={setFieldValue}
                          errors={
                            errors.equipments &&
                            errors.equipments[index]?.outlet_id
                          }
                          touched={
                            touched.equipments &&
                            touched.equipments[index]?.outlet_id
                          }
                          disabled={isEditMode == "edit" && !isEditing}
                          endAdornment={
                            equipment?.outlet_id ? (
                              <IconButton
                                sx={{
                                  position: "relative",
                                  right: "10px",
                                }}
                                onClick={() =>
                                  setFieldValue(
                                    `equipments[${index}].outlet_id`,
                                    ""
                                  )
                                }
                              >
                                <ClearIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            ) : null
                          }
                          onChange={(event) => {
                            const selectedValue = event.target.value;
                            handleChange({
                              target: {
                                name: `equipments[${index}].outlet_id`,
                                value: selectedValue,
                              },
                            });
                            setFieldValue(
                              `equipments[${index}].outlet_id`,
                              selectedValue
                            );
                          }}
                        />
                      </div>
                      <InputWithLabel
                        className={`${
                          isEditMode === "edit" && !isEditing
                            ? "text-textGrey border border-dashed "
                            : "text-black  border hover:border-themeBtn"
                        }  ${
                          errors.equipments?.[index]?.on_site_name &&
                          touched.equipments?.[index]?.on_site_name
                            ? `border border-red`
                            : `border border-borderTheme/80 focus-within:border-themeBtn `
                        }  transition duration-300 ease-in-out`}
                        idFromName={`equipments[${index}].on_site_name`}
                        label="On-Site Name"
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={equipment.on_site_name}
                        disabled={isEditMode == "edit" && !isEditing}
                        errors={
                          errors.equipments?.[index]?.on_site_name &&
                          touched.equipments?.[index]?.on_site_name
                        }
                        errorsText={errors.equipments?.[index]?.on_site_name}
                      />
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"
                      >
                        <div>
                          <SelectWithLabel
                            idFromName={`equipments[${index}].product_id`}
                            placeholder="Equipment"
                            options={productOptions}
                            value={equipment?.product_id || ""}
                            setFieldValue={setFieldValue}
                            errors={
                              errors.equipments &&
                              errors.equipments[index]?.product_id
                            }
                            touched={
                              touched.equipments &&
                              touched.equipments[index]?.product_id
                            }
                            disabled={isEditMode == "edit" && !isEditing}
                            endAdornment={
                              equipment?.product_id ? (
                                <IconButton
                                  sx={{
                                    position: "relative",
                                    right: "10px",
                                  }}
                                  onClick={() =>
                                    setFieldValue(
                                      `equipments[${index}].product_id`,
                                      ""
                                    )
                                  }
                                >
                                  <ClearIcon sx={{ fontSize: "16px" }} />
                                </IconButton>
                              ) : null
                            }
                            onChange={(event) => {
                              const selectedValue = event.target.value;
                              handleChange({
                                target: {
                                  name: `equipments[${index}].product_id`,
                                  value: selectedValue,
                                },
                              });
                              setFieldValue(
                                `equipments[${index}].product_id`,
                                selectedValue
                              );
                            }}
                          />
                        </div>
                        <div>
                          <DatePicker
                            value={
                              values.equipments[index].purchase_date
                                ? moment(values.equipments[index].purchase_date)
                                : null
                            }
                            onChange={(date) =>
                              setFieldValue(
                                `equipments[${index}].purchase_date`,
                                date ? date.toDate() : null
                              )
                            }
                            disabled={isEditMode === "edit" && !isEditing}
                            format="DD/MM/YYYY"
                            icon={null}
                            error={errors.equipments?.[index]?.purchase_date}
                            touched={touched.equipments?.[index]?.purchase_date}
                          />
                        </div>
                      </div>
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 mr-2 accent-themeBtn"
                          checked={equipment.warranty}
                          disabled={isEditMode == "edit" && !isEditing}
                          onChange={(e) =>
                            handleChange({
                              target: {
                                name: `equipments[${index}].warranty`,
                                value: e.target.checked,
                              },
                            })
                          }
                        />
                        <span
                          className={` ${
                            isEditMode == "edit" && !isEditing
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          Include Warranty
                        </span>
                      </label>
                      {equipment?.warranty == "1" && (
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <SelectWithLabel
                              idFromName="warrantyPeriod"
                              placeholder="Warranty Period (Months)"
                              options={[
                                { value: "3", label: "3 Months" },
                                { value: "6", label: "6 Months" },
                                { value: "12", label: "12 Months" },
                                { value: "24", label: "24 Months" },
                              ]}
                              value={equipment?.warrantyPeriod || ""}
                              setFieldValue={setFieldValue}
                              errors={
                                errors.equipments?.[index]?.warrantyPeriod
                              }
                              touched={
                                touched.equipments?.[index]?.warrantyPeriod
                              }
                              disabled={isEditMode == "edit" && !isEditing}
                              endAdornment={
                                equipment?.warrantyPeriod ? (
                                  <IconButton
                                    sx={{
                                      position: "relative",
                                      right: "10px",
                                    }}
                                    onClick={() =>
                                      setFieldValue(
                                        `equipments[${index}].warrantyPeriod`,
                                        ""
                                      )
                                    }
                                  >
                                    <ClearIcon sx={{ fontSize: "16px" }} />
                                  </IconButton>
                                ) : null
                              }
                              onChange={(event) => {
                                const selectedValue = event.target.value;
                                handleChange({
                                  target: {
                                    name: `equipments[${index}].warrantyPeriod`,
                                    value: selectedValue,
                                  },
                                });
                                setFieldValue(
                                  `equipments[${index}].warrantyPeriod`,
                                  selectedValue
                                );
                              }}
                            />
                          </div>
                          <InputWithLabel
                            className={`${
                              isEditMode === "edit" && !isEditing
                                ? "text-textGrey border border-dashed "
                                : "text-black  border hover:border-themeBtn"
                            }  ${
                              errors.equipments?.[index]?.serialNumber &&
                              touched.equipments?.[index]?.serialNumber
                                ? `border border-red`
                                : `border border-borderTheme/80 focus-within:border-themeBtn `
                            }  transition duration-300 ease-in-out`}
                            idFromName={`equipments[${index}].serialNumber`}
                            label="Serial Number"
                            type="text"
                            name={`equipments[${index}].serialNumber`}
                            value={equipment.serialNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isEditMode == "edit" && !isEditing}
                            errors={
                              errors.equipments?.[index]?.serialNumber &&
                              touched.equipments?.[index]?.serialNumber
                            }
                            errorsText={
                              errors.equipments?.[index]?.serialNumber
                            }
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="flex items-center justify-center my-4">
                    <div className="flex-grow border-t border-bgBtnGray"></div>
                    <button
                      type="button"
                      onClick={() => {
                        const newEquipment = {
                          outlet_id: "",
                          product_id: "",
                          on_site_name: "",
                          purchase_date: dayjs(),
                          warranty: false,
                          warrantyPeriod: "",
                          serialNumber: "",
                        };
                        handleChange({
                          target: {
                            name: "equipments",
                            value: [...values.equipments, newEquipment],
                          },
                        });
                      }}
                      disabled={isEditMode == "edit" && !isEditing}
                      className={`bg-bgBtnGray w-32 h-10 rounded-4xl flex justify-center items-center shadow-lg mx-4 px-4 py-2 ${
                        isEditMode == "edit" && !isEditing
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <Plus className="fill-black mr-2" />
                      <p className="font-Proxima text-sm">ADD MORE</p>
                    </button>
                    <div className="flex-grow border-t border-bgBtnGray"></div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-3">
                  <DefaultPrimaryButton
                    type="button"
                    value="Cancel"
                    onClick={() => navigate(-1)}
                  />
                  {isEditMode === "edit" ? (
                    isEditing ? (
                      <>
                        <DefaultButton type="submit" value="Save" />
                      </>
                    ) : (
                      <DefaultButton
                        value="Edit"
                        onClick={handleEditClick}
                        type="submit"
                      />
                    )
                  ) : (
                    <DefaultButton type="submit" value="Save" />
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {showConfirmationModal && (
        <ConfirmationModel
          confirmText={mode === "edit" ? "Edit" : "Create"}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={() => handleSubmit(formValues)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default AddNewEquipment;
